import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@fluentui/react-components';
import { useAuth } from './GoogleAuth';
import tokenManager from '../utils/tokenManager';
import './NutChallenge.css';

const API = '/api/nut';
const formatNorwegianDateTime = (value) => new Intl.DateTimeFormat('nb-NO', {
  timeZone: 'Europe/Oslo',
  dateStyle: 'medium',
  timeStyle: 'medium',
}).format(new Date(value));

const formatElapsed = (seconds) => {
  if (seconds === null) return null;
  return {
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
  };
};

const getOsloDateTimeInputValue = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}T${value.hour}:${value.minute}`;
};

const SpermGraphic = ({ className, style }) => (
  <svg className={`sperm ${className}`} style={style} viewBox="0 0 132 76" aria-hidden="true">
    <ellipse cx="23" cy="38" rx="15" ry="12" />
    <path d="M38 38c16-1 15-20 32-18 16 2 7 23 27 25 12 1 15-7 24-12" />
  </svg>
);

export default function NutChallenge() {
  const { user, loading, signIn } = useAuth();
  const [elapsedSeconds, setElapsedSeconds] = useState(null);
  const [statusLoadedAt, setStatusLoadedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [splashId, setSplashId] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({ name: '', message: '' });
  const [commentSaving, setCommentSaving] = useState(false);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replySavingId, setReplySavingId] = useState(null);
  const [nutAt, setNutAt] = useState(getOsloDateTimeInputValue);
  const isAdmin = user?.email === 'san.eriksen@gmail.com' || user?.email === 'admin@localhost';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Nut Counter | Sander Eriksen';
    return () => { document.title = previousTitle; };
  }, []);

  const loadStatus = async () => {
    const response = await fetch(`${API}/status`);
    if (!response.ok) throw new Error('Could not load challenge status');
    const data = await response.json();
    setElapsedSeconds(data.elapsed_seconds);
    setStatusLoadedAt(Date.now());
  };

  const loadLog = async () => {
    const token = tokenManager.getToken();
    if (!token || !isAdmin) return;
    const response = await fetch(`${API}/log`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) return;
    setEntries(await response.json());
  };

  const loadComments = async () => {
    const response = await fetch(`${API}/comments`);
    if (!response.ok) throw new Error('Could not load comments');
    setComments(await response.json());
  };

  useEffect(() => {
    loadStatus().catch(console.error);
    loadComments().catch(console.error);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isAdmin) loadLog();
  }, [isAdmin]);

  const elapsed = useMemo(() => formatElapsed(elapsedSeconds === null ? null : elapsedSeconds + Math.floor((now - statusLoadedAt) / 1000)), [elapsedSeconds, now, statusLoadedAt]);

  const recordNut = async () => {
    if (!window.confirm('Record this nut in your private log?')) return;
    const token = tokenManager.getToken();
    if (!token) return;
    setSaving(true);
    try {
      const response = await fetch(`${API}/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ occurred_at: new Date(nutAt).toISOString() }),
      });
      if (!response.ok) throw new Error('Could not record reset');
      const entry = await response.json();
      setElapsedSeconds(0);
      setStatusLoadedAt(Date.now());
      setEntries((current) => [entry, ...current]);
      setNow(Date.now());
      setNutAt(getOsloDateTimeInputValue());
    } catch (error) {
      console.error(error);
      window.alert('Could not record the nut.');
    } finally {
      setSaving(false);
    }
  };

  const triggerSplash = () => {
    setSplashId((current) => current + 1);
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const overtone = context.createOscillator();
    const gain = context.createGain();
    const overtoneGain = context.createGain();
    oscillator.type = 'sine';
    overtone.type = 'sine';
    oscillator.frequency.setValueAtTime(110, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(58, context.currentTime + 0.42);
    overtone.frequency.setValueAtTime(220, context.currentTime);
    overtone.frequency.exponentialRampToValueAtTime(110, context.currentTime + 0.24);
    gain.gain.setValueAtTime(0.001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.13, context.currentTime + 0.035);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.5);
    overtoneGain.gain.setValueAtTime(0.001, context.currentTime);
    overtoneGain.gain.exponentialRampToValueAtTime(0.045, context.currentTime + 0.02);
    overtoneGain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.28);
    oscillator.connect(gain).connect(context.destination);
    overtone.connect(overtoneGain).connect(context.destination);
    oscillator.start();
    overtone.start();
    oscillator.stop(context.currentTime + 0.52);
    overtone.stop(context.currentTime + 0.3);
    window.setTimeout(() => context.close(), 650);
  };

  const submitComment = async (event) => {
    event.preventDefault();
    if (!commentForm.name.trim() || !commentForm.message.trim()) return;
    setCommentSaving(true);
    try {
      const response = await fetch(`${API}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(commentForm),
      });
      if (!response.ok) throw new Error('Could not post comment');
      const comment = await response.json();
      setComments((current) => [comment, ...current]);
      setCommentForm({ name: '', message: '' });
    } catch (error) {
      console.error(error);
      window.alert('Could not post your comment.');
    } finally {
      setCommentSaving(false);
    }
  };

  const submitReply = async (event, commentId) => {
    event.preventDefault();
    const message = replyDrafts[commentId]?.trim();
    const token = tokenManager.getToken();
    if (!message || !token) return;
    setReplySavingId(commentId);
    try {
      const response = await fetch(`${API}/comments/${commentId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message }),
      });
      if (!response.ok) throw new Error('Could not post reply');
      const reply = await response.json();
      setComments((current) => current.map((comment) => comment.id === commentId ? { ...comment, reply } : comment));
      setReplyDrafts((current) => ({ ...current, [commentId]: '' }));
    } catch (error) {
      console.error(error);
      window.alert('Could not post your reply.');
    } finally {
      setReplySavingId(null);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment and its reply?')) return;
    const token = tokenManager.getToken();
    if (!token) return;
    try {
      const response = await fetch(`${API}/comments/${commentId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Could not delete comment');
      setComments((current) => current.filter((comment) => comment.id !== commentId));
    } catch (error) {
      console.error(error);
      window.alert('Could not delete the comment.');
    }
  };

  return (
    <main className="nut-page">
      <SpermGraphic className="sperm-one" />
      <SpermGraphic className="sperm-two" />
      <SpermGraphic className="sperm-three" />
      <SpermGraphic className="sperm-four" />
      <SpermGraphic className="sperm-five" />
      <SpermGraphic className="sperm-six" />
      <SpermGraphic className="sperm-seven" />
      <SpermGraphic className="sperm-eight" />
      <SpermGraphic className="sperm-nine" />
      <SpermGraphic className="sperm-ten" />
      {splashId > 0 && (
        <div className="nut-splash" key={splashId} aria-hidden="true">
          {Array.from({ length: 22 }, (_, index) => <i key={index} style={{ '--i': index }} />)}
          {Array.from({ length: 12 }, (_, index) => <SpermGraphic key={`swimmer-${index}`} className="sperm-sprint" style={{ '--i': index }} />)}
        </div>
      )}
      <section className="nut-counter" aria-live="polite">
        <p className="nut-kicker">No Nut Challenge</p>
        <h1>Time since nut</h1>
        {elapsed ? (
          <button type="button" className="nut-clock" onClick={triggerSplash} aria-label={`${elapsed.days} days, ${elapsed.hours} hours, ${elapsed.minutes} minutes, ${elapsed.seconds} seconds. Tap for a splash.`}>
            {Object.entries(elapsed).map(([unit, value]) => (
              <div className="nut-unit" key={unit}>
                <strong>{String(value).padStart(2, '0')}</strong>
                <span>{unit}</span>
              </div>
            ))}
          </button>
        ) : (
          <p className="nut-empty">The clock starts when Sander logs the first nut.</p>
        )}
      </section>

      {!loading && (
        <section className="nut-admin">
          {isAdmin ? (
            <>
              <div className="nut-record-control">
                <label>When did it happen?
                  <input type="datetime-local" value={nutAt} onChange={(event) => setNutAt(event.target.value)} />
                </label>
                <Button appearance="primary" onClick={recordNut} disabled={saving || !nutAt}>{saving ? 'Recording…' : 'Record nut'}</Button>
              </div>
              <div className="nut-log">
                <h2>Private nut log</h2>
                {entries.length ? <ol>{entries.map((entry) => <li key={entry.id}>{formatNorwegianDateTime(entry.occurred_at)}</li>)}</ol> : <p>No resets recorded.</p>}
              </div>
            </>
          ) : !user && <Button appearance="subtle" onClick={signIn}>Admin sign in</Button>}
        </section>
      )}

      <section className="nut-guestbook" aria-labelledby="guestbook-title">
        <p className="nut-kicker">Guestbook</p>
        <h2 id="guestbook-title">Leave some motivation</h2>
        <p>Keep it kind. Everyone can read these.</p>
        <form onSubmit={submitComment}>
          <label>
            Name
            <input value={commentForm.name} maxLength="48" required onChange={(event) => setCommentForm((current) => ({ ...current, name: event.target.value }))} />
          </label>
          <label>
            Message
            <textarea value={commentForm.message} maxLength="500" required rows="3" onChange={(event) => setCommentForm((current) => ({ ...current, message: event.target.value }))} />
          </label>
          <Button appearance="primary" type="submit" disabled={commentSaving}>{commentSaving ? 'Posting…' : 'Post comment'}</Button>
        </form>
        <div className="nut-comments">
          {comments.length ? comments.map((comment) => (
            <article key={comment.id}>
              <header><strong>{comment.name}</strong><time dateTime={comment.created_at}>{formatNorwegianDateTime(comment.created_at)}</time></header>
              <p>{comment.message}</p>
              {comment.reply && <aside className="nut-reply"><strong>Sander</strong><p>{comment.reply.message}</p></aside>}
              {isAdmin && (
                <div className="nut-comment-admin">
                  <form onSubmit={(event) => submitReply(event, comment.id)}>
                    <input aria-label={`Reply to ${comment.name}`} placeholder={comment.reply ? 'Edit reply' : 'Reply as Sander'} value={replyDrafts[comment.id] || ''} maxLength="500" onChange={(event) => setReplyDrafts((current) => ({ ...current, [comment.id]: event.target.value }))} />
                    <Button appearance="secondary" type="submit" disabled={replySavingId === comment.id}>{replySavingId === comment.id ? 'Saving…' : comment.reply ? 'Update reply' : 'Reply'}</Button>
                  </form>
                  <button type="button" className="nut-delete-comment" onClick={() => deleteComment(comment.id)}>Delete comment</button>
                </div>
              )}
            </article>
          )) : <p className="nut-no-comments">Be the first to leave a message.</p>}
        </div>
      </section>
    </main>
  );
}
